/**
 * useFeedback - Sensory Feedback Hook
 * 
 * Provides haptic feedback, sound effects, and visual feedback
 * for user interactions. Future-ready for haptic API integration.
 * 
 * @hook
 * @example
 * const { playSuccess, playError, vibrate } = useFeedback();
 * playSuccess();
 */

import { useCallback } from 'react';

export interface FeedbackOptions {
  intensity?: 'light' | 'medium' | 'strong';
  duration?: number;
  sound?: boolean;
  haptic?: boolean;
}

export function useFeedback() {
  // Haptic feedback (future-ready for Vibration API)
  const vibrate = useCallback((pattern: number | number[] = 50) => {
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
      } catch (error) {
        // Silently fail if vibration is not supported
      }
    }
  }, []);

  // Sound feedback (future-ready for Web Audio API)
  const playSound = useCallback((type: 'success' | 'error' | 'click' | 'notification') => {
    // Placeholder for future Web Audio API implementation
    // For now, we can use HTML5 Audio or Web Audio API
    // This is a no-op but ready for implementation
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useFeedback] Sound: ${type}`);
    }
  }, []);

  // Success feedback
  const playSuccess = useCallback((options?: FeedbackOptions) => {
    vibrate([50, 30, 50]);
    playSound('success');
  }, [vibrate, playSound]);

  // Error feedback
  const playError = useCallback((options?: FeedbackOptions) => {
    vibrate([100, 50, 100]);
    playSound('error');
  }, [vibrate, playSound]);

  // Click feedback
  const playClick = useCallback((options?: FeedbackOptions) => {
    vibrate(10);
    playSound('click');
  }, [vibrate, playSound]);

  // Notification feedback
  const playNotification = useCallback((options?: FeedbackOptions) => {
    vibrate([100, 50, 100, 50, 100]);
    playSound('notification');
  }, [vibrate, playSound]);

  // Call action feedback
  const playCall = useCallback(() => {
    vibrate([50, 30, 50]);
    playSound('notification');
  }, [vibrate, playSound]);

  // WhatsApp action feedback
  const playWhatsApp = useCallback(() => {
    vibrate([30, 20, 30]);
    playSound('notification');
  }, [vibrate, playSound]);

  // Mark done feedback
  const playMarkDone = useCallback(() => {
    vibrate([50, 30, 50, 30, 50]);
    playSound('success');
  }, [vibrate, playSound]);

  return {
    vibrate,
    playSound,
    playSuccess,
    playError,
    playClick,
    playNotification,
    playCall,
    playWhatsApp,
    playMarkDone,
  };
}

